import React, { FC, useEffect, useState } from 'react';
import { ItinerarySearchAlternativesRequest } from 'generated/graphql';
import {
  usePersistedSessionState,
  useTrackElement,
} from '@edreamsodigeo/frontend-core';
import { useFlowTools } from 'flowTools/useFlowTools';
import useCreateShoppingCart from 'clientState/hooks/useCreateShoppingCart';
import { useAlternativeItineraryMutation } from 'clientState/hooks/useAlternativeItineraryMutation';
import { getValuePropositionList } from 'clientState/providers/AlternativeItineraryProvider/helper';
import {
  getPaxPageUrl,
  getOF1SessionState,
  getSearchRequest,
  getSearchUrl,
  transformAlternativeItinerary,
  getUpdatedSearch,
} from './helpers';
import {
  AlternativeFlightLayerProps,
  AlternativeFlightLayerWrapperProps,
} from './types';
import AvailabilityModal from '@components/Results/Components/Modals/AvailabilityModal';
import { AlternativeItineraryDoubleCardLayerBase } from '@components/AlternativeItineraryDoubleCard/components/AlternativeItineraryDoubleCardLayer/AlternativeItineraryDoubleCardLayer';
import itineraryResolver from 'clientState/resolvers/itinerary';
import { ThreeColumnsItineraryType } from '@components/Results/Components/ThreeColumnsItinerary/types';
import { AlternativeItineraryValuePropositionType } from 'clientState/providers/AlternativeItineraryProvider/types';
import {
  MEMBER_PRICE_POLICY_DISCOUNTED,
  MEMBER_PRICE_POLICY_UNDISCOUNTED,
} from '../Shared/FeeSelector/FeeTypeName/feeTypeConstantNames';
import { BrokenFlowModalComponent } from './AlternativeFlightBrokenFlow';
import {
  AlternativeFlightType,
  PageType,
  useTrackingHandler,
} from 'clientState/providers/AlternativeItineraryProvider/hooks';
import { usePersistedFlightSelection } from 'clientState/hooks/usePersistedFlightSelection/usePersistedFlightSelection';

const AlternativeFlightBaseLayerWrapper: FC<
  AlternativeFlightLayerWrapperProps
> = ({
  nonIssuable,
  searchId,
  itineraryKey,
  feeSelected,
  withDiscount,
  isBrokenFlow,
  selectedItinerary,
  carriers,
  overrideAction,
  goToSummary,
  showLoadingBar,
  pageName,
  simpleUI,
}) => {
  const [searchParameters] = usePersistedSessionState('searchParameters');
  const mutationRequest: ItinerarySearchAlternativesRequest = {
    eligibleForDiscount: withDiscount,
    itineraryCarriers: carriers,
    itineraryId:
      itineraryKey || selectedItinerary.key || selectedItinerary.id || '',
    searchId: Number(searchId) || 0,
    searchRequest: getSearchRequest(searchParameters),
  };

  const onContinue = () => {
    const searchUrl = getSearchUrl(searchParameters);
    window.location.assign(searchUrl);
  };

  return selectedItinerary &&
    selectedItinerary.legs.length > 0 &&
    selectedItinerary.fees.length > 0 ? (
    <AlternativeFlightBaseLayer
      nonIssuable={nonIssuable}
      searchId={searchId}
      feeSelected={feeSelected}
      selectedItinerary={selectedItinerary}
      mutationRequest={mutationRequest}
      onContinue={onContinue}
      withDiscount={withDiscount}
      isBrokenFlow={isBrokenFlow}
      overrideAction={overrideAction}
      pageName={pageName}
      goToSummary={goToSummary}
      showLoadingBar={showLoadingBar}
      simpleUI={simpleUI}
    />
  ) : null;
};

const AlternativeFlightBaseLayer: FC<AlternativeFlightLayerProps> = ({
  nonIssuable,
  searchId,
  feeSelected,
  selectedItinerary,
  mutationRequest,
  onContinue,
  withDiscount,
  isBrokenFlow = false,
  overrideAction,
  goToSummary,
  showLoadingBar,
  pageName,
  simpleUI,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [searchParameters] = usePersistedSessionState('searchParameters');
  const [loadSend, setLoadSend] = useState(false);
  const { isDesktop } = useFlowTools();
  const [, setFlightSelectionData] = usePersistedFlightSelection();
  const {
    mutate,
    loading,
    errors,
    searchId: alternativeSearchId,
    alternativeItinerary,
    carriers,
    locations,
    carbonFootprints,
  } = useAlternativeItineraryMutation();
  const {
    mutate: mutateCreateShoppingCart,
    data: createShoppingCartData,
    loading: createShoppingCartLoading,
  } = useCreateShoppingCart();
  const newBookingId =
    createShoppingCartData?.createShoppingCart?.shoppingCart?.bookingId;
  const feeResultsSelected =
    feeSelected === 'PRIME_PRICE'
      ? MEMBER_PRICE_POLICY_DISCOUNTED
      : MEMBER_PRICE_POLICY_UNDISCOUNTED;
  const parsedAlternativeItinerary = alternativeItinerary
    ? (itineraryResolver(
        transformAlternativeItinerary(
          alternativeItinerary,
          feeResultsSelected,
          carriers,
          locations,
          carbonFootprints
        ),
        0
      ) as ThreeColumnsItineraryType)
    : ({
        id: '0',
        fees: [
          {
            price: { amount: 0, currency: 'EUR' },
            type: { id: 'MEMBER_PRICE_POLICY_UNDISCOUNTED' },
          },
          {
            price: { amount: 0, currency: 'EUR' },
            type: { id: 'MEMBER_PRICE_POLICY_DISCOUNTED' },
          },
        ],
        legs: [],
        ticketsLeft: 0,
        transportTypes: [],
        price: { amount: 0, currency: 'EUR' },
        priceSortingPosition: 0,
      } as ThreeColumnsItineraryType);
  const { valuePropositions, additionalInformation } = alternativeItinerary
    ? getValuePropositionList(
        searchParameters.searchSegments,
        selectedItinerary,
        parsedAlternativeItinerary,
        withDiscount
      )
    : {
        valuePropositions: [
          'social',
        ] as AlternativeItineraryValuePropositionType[],
        additionalInformation: {},
      };
  const valueProposition = valuePropositions[0];

  const errorDisplayTracking = useTrackElement(
    ['display'],
    `alternative-flights-payment-error-searchId-${searchId}-itineraryId-${selectedItinerary.key}`
  );
  const trackingHandler = useTrackingHandler({
    selectedItinerary,
    alternativeItinerary: parsedAlternativeItinerary,
    nonIssuable,
    pageName,
    alternativeItineraryPosition: 0,
    valueProposition,
    valuePropositions,
    alternativeFlightType: isBrokenFlow
      ? AlternativeFlightType.BrokenFlow
      : AlternativeFlightType.NonBookable,
  });

  const onChange = () => {
    if (goToSummary) {
      const { search = '', hash } = window.location;
      const params = new URLSearchParams(search);
      const flightSummaryParams = {
        searchId: alternativeSearchId,
        fareItineraryKey: alternativeItinerary?.key,
        segmentKeys: alternativeItinerary?.legs?.reduce<number[]>(
          (acc, actual) => {
            return acc.concat(actual?.segmentKeys);
          },
          []
        ),
        feeTypeId: 'MEMBER_PRICE_POLICY_DISCOUNTED',
      };
      // It is important to set this in the session state so OF1 does
      // what it needs to do when back in the summary
      setFlightSelectionData(
        getOF1SessionState(alternativeSearchId, alternativeItinerary)
      );
      const base64Params = btoa(JSON.stringify(flightSummaryParams));
      // Add/remove query param to force a reload in case we are in the summary page
      const updatedSearch = hash?.includes('#checkout_flight_summary_results')
        ? getUpdatedSearch(params)
        : search;
      window.location.assign(
        `/travel/${updatedSearch}#checkout_flight_summary_results/${base64Params}/`
      );
    } else {
      trackingHandler('select', 'alternative', 'button');
      mutateCreateShoppingCart({
        searchId: alternativeSearchId?.toString() || '',
        alternativeItinerary: parsedAlternativeItinerary,
        resident: mutationRequest.searchRequest?.itinerary?.resident,
      });
    }
  };

  const onContinueHandler = () => {
    trackingHandler('discard', 'button', 'CR');
    if (pageName === PageType.Pax && !nonIssuable) {
      setIsOpen(false);
    } else {
      onContinue();
    }
  };

  const onClose = () => {
    if (!nonIssuable) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (
      selectedItinerary &&
      mutationRequest &&
      !loading &&
      !alternativeItinerary &&
      !errors
    ) {
      mutate({
        itinerarySearchAlternativesRequest: mutationRequest,
      });
    }
  }, [
    selectedItinerary,
    mutationRequest,
    loading,
    alternativeItinerary,
    errors,
    mutate,
  ]);

  useEffect(() => {
    if (newBookingId) {
      const paxUrl = getPaxPageUrl(isDesktop, newBookingId);
      setFlightSelectionData(
        getOF1SessionState(alternativeSearchId, alternativeItinerary)
      );
      setIsOpen(false);
      window.location.assign(paxUrl);
    }
  }, [
    newBookingId,
    isDesktop,
    setFlightSelectionData,
    alternativeSearchId,
    alternativeItinerary,
  ]);

  useEffect(() => {
    if (!errors && !loadSend && alternativeItinerary) {
      setLoadSend(true);
      trackingHandler('onLoad', '');
    }
  }, [errors, loadSend, alternativeItinerary, setLoadSend, trackingHandler]);

  return !errors ? (
    <AlternativeItineraryDoubleCardLayerBase
      isOpen={isOpen}
      onClose={onClose}
      itinerary={parsedAlternativeItinerary}
      selectedItinerary={selectedItinerary}
      discountAmount={undefined}
      valueProposition={valueProposition}
      valuePropositions={valuePropositions}
      additionalInformation={additionalInformation}
      nonIssuable={nonIssuable}
      isBrokenFlow={isBrokenFlow}
      isUnavailable={nonIssuable}
      hasCloseIcon={!nonIssuable}
      showContinueButton={nonIssuable}
      loading={loading}
      onContinue={onContinueHandler}
      onChange={onChange}
      showLoadingBar={showLoadingBar}
      isShoppingCartLoading={createShoppingCartLoading}
      overrideAction={overrideAction}
      trackingHandler={trackingHandler}
      simpleUI={simpleUI}
      pageName={pageName}
    />
  ) : !isBrokenFlow ? (
    <div ref={errorDisplayTracking}>
      <AvailabilityModal open={true} onClose={() => {}} onClick={onContinue} />
    </div>
  ) : (
    <BrokenFlowModalComponent />
  );
};

export default AlternativeFlightBaseLayerWrapper;
